/* A short ASCII decode confined to the measured label. The real text remains
   in the accessibility tree; the decorative layer never changes its layout. */
(() => {
  if (customElements.get('product-list-command')) return;
  const SYMBOLS = '0123456789/#%+*=<>[]{}|:;';
  class ProductListCommand extends HTMLElement {
    connectedCallback() {
      this.disconnect();
      this.lifecycle = new AbortController();
      const options = { signal: this.lifecycle.signal };
      this.text = this.querySelector('[data-command-text]');
      this.noise = this.querySelector('.pl-command-noise');
      const summary = this.closest('summary') || (this.hasAttribute('data-scramble-surface') ? this : null);
      if (!this.text || !this.noise || !summary) return;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.pointer = matchMedia('(hover: hover) and (pointer: fine)');
      this.motion.addEventListener('change', () => this.stop(), options);
      this.pointer.addEventListener('change', () => this.stop(), options);
      summary.addEventListener('pointerenter', () => {
        if (this.dataset.scrambleHover === 'true' && this.pointer.matches) this.play(288);
      }, options);
      summary.addEventListener('pointerleave', () => this.stop(), options);
      summary.addEventListener('pointerdown', () => this.stop(), options);
      summary.addEventListener('keydown', () => this.stop(), options);
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.stop(); }, options);
      window.addEventListener('pagehide', () => this.stop(), options);
      this.dimensions = this.getBoundingClientRect();
      this.resize = new ResizeObserver(() => {
        const next = this.getBoundingClientRect();
        if (Math.abs(next.width - this.dimensions.width) > .5 || Math.abs(next.height - this.dimensions.height) > .5) this.stop();
        this.dimensions = next;
      });
      this.resize.observe(this);
      if (this.dataset.scramble === 'true') {
        this.visibility = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          this.visibility.disconnect();
          const signal = this.lifecycle.signal;
          document.fonts.ready.then(() => {
            if (!signal.aborted && this.isConnected) this.play(360);
          });
        }, { threshold: .1 });
        this.visibility.observe(this);
      }
    }

    play(duration) {
      if (this.motion.matches || this.running || document.hidden) return;
      const node = this.text.firstChild;
      if (!node || node.nodeType !== Node.TEXT_NODE || !node.textContent.trim()) return;
      const box = this.getBoundingClientRect();
      if (!box.width || !box.height) return;
      const value = node.textContent;
      const segments = typeof Intl.Segmenter === 'function'
        ? [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(value)].map(part => ({ text: part.segment, index: part.index }))
        : Array.from(value).map((text, i, chars) => ({ text, index: chars.slice(0, i).join('').length }));
      const lineHeight = parseFloat(getComputedStyle(this.text).lineHeight);
      const range = document.createRange();
      const fragment = document.createDocumentFragment();
      const cells = [];
      for (const part of segments) {
        range.setStart(node, part.index);
        range.setEnd(node, part.index + part.text.length);
        const rect = range.getBoundingClientRect();
        const cell = document.createElement('span');
        cell.className = 'pl-command-glyph';
        cell.style.left = `${rect.left - box.left}px`;
        cell.style.top = `${rect.top - box.top - ((lineHeight || rect.height) - rect.height) / 2}px`;
        cell.style.width = `${rect.width}px`;
        fragment.append(cell);
        cells.push({ cell, text: part.text, space: /^\s+$/.test(part.text), settle: .22 + Math.random() * .64, next: 0 });
      }
      range.detach();
      this.noise.replaceChildren(fragment);
      this.running = true;
      this.setAttribute('data-scrambling', '');
      const start = performance.now();
      const draw = now => {
        const elapsed = now - start;
        const progress = Math.min(1, elapsed / duration);
        if (progress >= 1) { this.stop(); return; }
        cells.forEach(part => {
          if (elapsed < part.next) return;
          // Independent clocks avoid a marching decode. Brief false locks make
          // the middle glitchy; the last 14% is an uninterrupted clean settle.
          const resolved = part.space || progress >= part.settle;
          const relapse = resolved && !part.space && progress < .72 && Math.random() < .12;
          part.cell.textContent = resolved && !relapse ? part.text : SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)];
          part.next = elapsed + 18 + Math.random() * 24;
        });
        this.frame = requestAnimationFrame(draw);
      };
      draw(start);
    }

    stop() {
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.running = false;
      this.removeAttribute('data-scrambling');
      this.noise?.replaceChildren();
    }
    disconnect() {
      this.stop();
      this.lifecycle?.abort();
      this.resize?.disconnect();
      this.visibility?.disconnect();
    }
    disconnectedCallback() { this.disconnect(); }
  }
  customElements.define('product-list-command', ProductListCommand);
})();
